/**
 * Чтение таблицы продавца: xlsx, csv, tsv — без сторонних библиотек.
 *
 * Продавец приносит выгрузку «в любой форме»: из WB, Ozon, Lamoda, 1С или
 * руками в Excel. Задача парсера — отдать прямоугольник строк; что значит
 * каждая колонка, решает экран сопоставления.
 *
 * xlsx — это zip с XML внутри. Zip разбираем сами (центральный каталог +
 * deflate через DecompressionStream, он есть и в браузере, и в Bun), XML листа
 * читаем регулярками: структура листа простая, а DOMParser есть не везде.
 * Старый двоичный .xls (BIFF) не поддерживаем — просим пересохранить в xlsx.
 */

export type SpreadsheetRow = {
  /** Номер строки в файле, как его видит продавец в Excel (с 1). */
  n: number
  cells: string[]
}

export type Spreadsheet = {
  /** Имя листа (для xlsx) или формат файла. */
  sheet: string
  /** Все непустые строки файла, включая строку заголовков. */
  rows: SpreadsheetRow[]
}

export class SpreadsheetError extends Error {
  constructor(
    public readonly code: "legacy-xls" | "unsupported" | "empty" | "broken",
    message: string,
  ) {
    super(message)
    this.name = "SpreadsheetError"
  }
}

// ── Кодировки ─────────────────────────────────────────────────────────────

// windows-1251, верхняя половина 0x80–0xBF (0xC0–0xFF — это А..я подряд).
const CP1251_HIGH =
  "ЂЃ‚ѓ„…†‡€‰Љ‹ЊЌЋЏђ‘’“”•–—\u0098™љ›њќћџ ЎўЈ¤Ґ¦§Ё©Є«¬­®Ї°±Ііґµ¶·ё№є»јЅѕї"

/** windows-1251 → строка. TextDecoder в Bun эту кодировку не знает, поэтому своя таблица. */
export const decodeCp1251 = (bytes: Uint8Array): string => {
  let out = ""
  for (const byte of bytes) {
    if (byte < 0x80) out += String.fromCharCode(byte)
    else if (byte >= 0xc0) out += String.fromCharCode(0x0410 + byte - 0xc0)
    else out += CP1251_HIGH[byte - 0x80] ?? "?"
  }
  return out
}

/**
 * Текст файла: UTF-8 (с BOM или без), UTF-16 с BOM, иначе windows-1251 —
 * так сохраняет CSV русский Excel по умолчанию.
 */
export const decodeText = (bytes: Uint8Array): string => {
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder("utf-16le").decode(bytes.subarray(2))
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder("utf-16be").decode(bytes.subarray(2))
  const body = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? bytes.subarray(3) : bytes
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(body)
  } catch {
    return decodeCp1251(body)
  }
}

// ── CSV ───────────────────────────────────────────────────────────────────

/** Разбор CSV с кавычками («"» внутри поля удваивается) и переводами строк в полях. */
const parseDelimited = (text: string, delimiter: string): string[][] => {
  const rows: string[][] = []
  let row: string[] = []
  let field = ""
  let quoted = false
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'
          i += 1
        } else quoted = false
      } else field += ch
      continue
    }
    if (ch === '"' && field === "") quoted = true
    else if (ch === delimiter) {
      row.push(field)
      field = ""
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1
      row.push(field)
      rows.push(row)
      row = []
      field = ""
    } else field += ch
  }
  if (field !== "" || row.length) {
    row.push(field)
    rows.push(row)
  }
  return rows
}

/**
 * Разделитель: тот, что даёт одинаковое (и больше одного) число колонок в
 * первых строках. Русский Excel пишет «;», англоязычный — «,», выгрузки
 * маркетплейсов бывают с табуляцией.
 */
export const detectDelimiter = (text: string): string => {
  const sample = text.split(/\r?\n/).filter((line) => line.trim()).slice(0, 20).join("\n")
  let best = ","
  let bestScore = -1
  for (const delimiter of [";", "\t", ",", "|"]) {
    const rows = parseDelimited(sample, delimiter).filter((r) => r.some((c) => c.trim()))
    if (!rows.length) continue
    const counts = rows.map((r) => r.length)
    const width = Math.max(...counts)
    if (width < 2) continue
    const consistent = counts.filter((c) => c === width).length / counts.length
    const score = consistent * 100 + width
    if (score > bestScore) {
      best = delimiter
      bestScore = score
    }
  }
  return best
}

export const parseCsv = (text: string, delimiter = detectDelimiter(text)): Spreadsheet => ({
  sheet: delimiter === "\t" ? "tsv" : "csv",
  rows: toRows(parseDelimited(text, delimiter)),
})

const toRows = (raw: string[][]): SpreadsheetRow[] => {
  const rows: SpreadsheetRow[] = []
  raw.forEach((cells, index) => {
    const clean = cells.map((cell) => cell.replace(/ /g, " ").trim())
    while (clean.length && clean[clean.length - 1] === "") clean.pop()
    if (clean.some(Boolean)) rows.push({ n: index + 1, cells: clean })
  })
  return rows
}

// ── ZIP ───────────────────────────────────────────────────────────────────

const inflateRaw = async (data: Uint8Array): Promise<Uint8Array> => {
  // Копия в свой ArrayBuffer: Blob не принимает срез общего буфера во всех средах.
  const copy = new Uint8Array(data.byteLength)
  copy.set(data)
  const stream = new Blob([copy.buffer]).stream().pipeThrough(
    new DecompressionStream("deflate-raw" as ConstructorParameters<typeof DecompressionStream>[0]),
  )
  return new Uint8Array(await new Response(stream).arrayBuffer())
}

/** Файлы zip-архива по имени. Читаем через центральный каталог — он надёжнее локальных заголовков. */
export const unzip = async (bytes: Uint8Array): Promise<Map<string, Uint8Array>> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let eocd = -1
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65557); i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) {
      eocd = i
      break
    }
  }
  if (eocd < 0) throw new SpreadsheetError("broken", "Файл повреждён: не удалось открыть архив xlsx")
  const count = view.getUint16(eocd + 10, true)
  let offset = view.getUint32(eocd + 16, true)
  const files = new Map<string, Uint8Array>()
  const names = new TextDecoder()
  for (let i = 0; i < count; i += 1) {
    if (view.getUint32(offset, true) !== 0x02014b50) break
    const method = view.getUint16(offset + 10, true)
    const compressedSize = view.getUint32(offset + 20, true)
    const nameLength = view.getUint16(offset + 28, true)
    const extraLength = view.getUint16(offset + 30, true)
    const commentLength = view.getUint16(offset + 32, true)
    const localOffset = view.getUint32(offset + 42, true)
    const name = names.decode(bytes.subarray(offset + 46, offset + 46 + nameLength))
    const localName = view.getUint16(localOffset + 26, true)
    const localExtra = view.getUint16(localOffset + 28, true)
    const start = localOffset + 30 + localName + localExtra
    const data = bytes.subarray(start, start + compressedSize)
    if (method === 0) files.set(name, data)
    else if (method === 8) files.set(name, await inflateRaw(data))
    offset += 46 + nameLength + extraLength + commentLength
  }
  return files
}

// ── XLSX ──────────────────────────────────────────────────────────────────

const ENTITIES: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" }
const unescapeXml = (value: string): string =>
  value.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, entity: string) => {
    if (entity[0] === "#") {
      const code = entity[1] === "x" || entity[1] === "X" ? Number.parseInt(entity.slice(2), 16) : Number(entity.slice(1))
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole
    }
    return ENTITIES[entity.toLowerCase()] ?? whole
  })

/** Текст всех <t> внутри узла (строка с форматированием состоит из нескольких прогонов). */
const textRuns = (xml: string): string => {
  let out = ""
  for (const match of xml.matchAll(/<(?:\w+:)?t(?:\s[^>]*)?>([\s\S]*?)<\/(?:\w+:)?t>/g)) out += match[1]
  return unescapeXml(out)
}

const attr = (tag: string, name: string): string | undefined =>
  tag.match(new RegExp(`\\s${name}="([^"]*)"`))?.[1]

/** «B12» → индекс колонки 1. */
const columnIndex = (ref: string): number => {
  const letters = ref.match(/^[A-Z]+/i)?.[0].toUpperCase() ?? "A"
  let index = 0
  for (const letter of letters) index = index * 26 + (letter.charCodeAt(0) - 64)
  return index - 1
}

/** Число из ячейки: без хвостов плавающей точки (1499.9999999 → 1500), как показывает Excel. */
const formatNumber = (raw: string): string => {
  const value = Number(raw)
  if (!Number.isFinite(value)) return raw
  return String(Number(value.toPrecision(15)))
}

const resolvePath = (base: string, target: string): string => {
  if (target.startsWith("/")) return target.slice(1)
  const parts = base.split("/").slice(0, -1)
  for (const segment of target.split("/")) {
    if (segment === "..") parts.pop()
    else if (segment !== ".") parts.push(segment)
  }
  return parts.join("/")
}

export const parseXlsx = async (bytes: Uint8Array): Promise<Spreadsheet> => {
  const files = await unzip(bytes)
  const text = (name: string) => {
    const data = files.get(name)
    return data ? new TextDecoder().decode(data) : ""
  }
  const workbook = text("xl/workbook.xml")
  if (!workbook) throw new SpreadsheetError("broken", "В архиве нет книги Excel (xl/workbook.xml)")

  // Первый лист книги: имя и путь через связи книги.
  const sheetTag = workbook.match(/<(?:\w+:)?sheet\s[^>]*>/)?.[0] ?? ""
  const sheetName = unescapeXml(attr(sheetTag, "name") ?? "Лист1")
  const relId = attr(sheetTag, "r:id") ?? sheetTag.match(/\s\w+:id="([^"]*)"/)?.[1]
  const rels = text("xl/_rels/workbook.xml.rels")
  let sheetPath = "xl/worksheets/sheet1.xml"
  if (relId) {
    for (const rel of rels.matchAll(/<Relationship\s[^>]*>/g)) {
      if (attr(rel[0], "Id") === relId) {
        const target = attr(rel[0], "Target")
        if (target) sheetPath = resolvePath("xl/workbook.xml", target)
      }
    }
  }
  const sheet = text(sheetPath) || text("xl/worksheets/sheet1.xml")
  if (!sheet) throw new SpreadsheetError("broken", "В книге Excel не найден лист с данными")

  const shared: string[] = []
  for (const si of text("xl/sharedStrings.xml").matchAll(/<(?:\w+:)?si>([\s\S]*?)<\/(?:\w+:)?si>/g)) {
    shared.push(textRuns(si[1] ?? ""))
  }

  const raw: { n: number; cells: string[] }[] = []
  let implicitRow = 0
  for (const rowMatch of sheet.matchAll(/<(?:\w+:)?row(\s[^>]*)?(?:\/>|>([\s\S]*?)<\/(?:\w+:)?row>)/g)) {
    const rowAttrs = rowMatch[1] ?? ""
    implicitRow = Number(attr(rowAttrs, "r")) || implicitRow + 1
    const cells: string[] = []
    let implicitCol = 0
    for (const cellMatch of (rowMatch[2] ?? "").matchAll(
      /<(?:\w+:)?c(\s[^>]*)?(?:\/>|>([\s\S]*?)<\/(?:\w+:)?c>)/g,
    )) {
      const cellAttrs = cellMatch[1] ?? ""
      const ref = attr(cellAttrs, "r")
      const col = ref ? columnIndex(ref) : implicitCol
      implicitCol = col + 1
      const type = attr(cellAttrs, "t")
      const body = cellMatch[2] ?? ""
      const v = body.match(/<(?:\w+:)?v>([\s\S]*?)<\/(?:\w+:)?v>/)?.[1]
      let value = ""
      if (type === "s") value = shared[Number(v)] ?? ""
      else if (type === "inlineStr") value = textRuns(body)
      else if (type === "b") value = v === "1" ? "ИСТИНА" : "ЛОЖЬ"
      else if (type === "str" || type === "e") value = unescapeXml(v ?? "")
      else value = v !== undefined ? formatNumber(unescapeXml(v)) : ""
      cells[col] = value
    }
    raw.push({ n: implicitRow, cells: Array.from(cells, (cell) => cell ?? "") })
  }

  const rows: SpreadsheetRow[] = []
  for (const row of raw) {
    const clean = row.cells.map((cell) => cell.replace(/ /g, " ").trim())
    while (clean.length && clean[clean.length - 1] === "") clean.pop()
    if (clean.some(Boolean)) rows.push({ n: row.n, cells: clean })
  }
  return { sheet: sheetName, rows }
}

// ── Точка входа ───────────────────────────────────────────────────────────

const isZip = (bytes: Uint8Array) => bytes[0] === 0x50 && bytes[1] === 0x4b
const isOle = (bytes: Uint8Array) =>
  bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0

/** Файл продавца → строки. Формат определяем по содержимому, а не по расширению. */
export const readSpreadsheet = async (bytes: Uint8Array, fileName = ""): Promise<Spreadsheet> => {
  if (!bytes.length) throw new SpreadsheetError("empty", "Файл пустой")
  let sheet: Spreadsheet
  if (isOle(bytes)) {
    throw new SpreadsheetError(
      "legacy-xls",
      "Старый формат Excel (.xls) не поддерживается: откройте файл и сохраните как «Книга Excel (.xlsx)» или CSV",
    )
  }
  if (isZip(bytes)) {
    if (/\.(ods|numbers)$/i.test(fileName)) {
      throw new SpreadsheetError("unsupported", "Сохраните таблицу в формате xlsx или CSV")
    }
    sheet = await parseXlsx(bytes)
  } else {
    const text = decodeText(bytes)
    if (/\.(xls)$/i.test(fileName) && /<html|<table/i.test(text.slice(0, 2000))) {
      throw new SpreadsheetError(
        "legacy-xls",
        "Файл .xls сохранён как веб-страница: откройте его в Excel и сохраните как «Книга Excel (.xlsx)»",
      )
    }
    sheet = parseCsv(text, /\.tsv$/i.test(fileName) ? "\t" : undefined)
  }
  if (!sheet.rows.length) throw new SpreadsheetError("empty", "В файле нет данных")
  return sheet
}
